/**
 * ETL tour runtime types and a hand-rolled validator.
 *
 * Every Tour produced by JSON, the ETL parser, or an AI agent must pass
 * {@link validateTour} before it is handed to the runtime. The validator
 * is the single point of truth for the tour schema contract — there are
 * no `zod` / `ajv` dependencies, and the returned object is deeply frozen
 * so the runtime cannot accidentally mutate authored content.
 */

import { DEFAULT_LIMITS, PRESS_KEY_WHITELIST, SAFE_SELECTOR_PATTERN } from './limits';
import type { EtlLimits } from './limits';
import type { Expr } from './expr-types';

export type LocatorRoleName =
  | 'button'
  | 'link'
  | 'textbox'
  | 'searchbox'
  | 'combobox'
  | 'checkbox'
  | 'radio'
  | 'switch'
  | 'tab'
  | 'menuitem'
  | 'option'
  | 'heading'
  | 'table'
  | 'grid'
  | 'row'
  | 'cell'
  | 'columnheader'
  | 'rowheader'
  | 'list'
  | 'listitem'
  | 'dialog'
  | 'alertdialog'
  | 'alert'
  | 'status'
  | 'img'
  | 'spinbutton';

/**
 * Per-plugin semantic-locator vocabulary. Each entry maps a stable
 * alias (e.g. `'idp-users.create-btn'`) to a concrete {@link Locator}.
 * The plugin's UI team owns this map; tour authors reference aliases
 * via `@plugin.<plugin-id>:<name>` or `@:<name>` (active plugin) and
 * stay decoupled from the underlying selector strategy.
 *
 * Alias names are kebab-and-dot identifiers — `[a-z][a-z0-9.-]*` —
 * scoped per-plugin. Conflicts across plugins are fine; the registry
 * keys on `(pluginId, name)`.
 */
export type TourLocatorVocabulary = Readonly<Record<string, Locator>>;

export interface Locator {
  testid?: string;
  role?: LocatorRoleName;
  name?: string;
  label?: string;
  text?: string;
  selector?: string;
  exact?: boolean;
  within?: Locator;
  nth?: number;
  /**
   * v2 locator-variable reference. Set by the parser when an author
   * writes `$NAME` where a locator is expected. The runner dereferences
   * this at step time by looking up `NAME` in the active variable
   * scope, which must hold a Locator (typically bound by
   * `let NAME = #testid`). Mutually exclusive with the strategy
   * fields above.
   */
  varRef?: string;
  /**
   * Semantic alias resolved through the host's locator registry.
   * `plugin === ''` means "active plugin" (resolved at runtime from
   * the running tour's `pluginId`). Set by the parser when an author
   * writes `@plugin.<plugin-id>:<name>` or `@:<name>`. The host
   * resolver dereferences the alias to a concrete Locator at step
   * time so renames in the plugin's `LOCATORS` vocabulary keep tours
   * working without edits.
   */
  alias?: { plugin: string; name: string };
}

/**
 * v1 step type names — the set the validator currently admits. v2 adds
 * many more (see {@link StepTypeV2}); they are typed in this file so
 * downstream consumers can `switch (step.type)` exhaustively, but the
 * v1 validator still rejects them until later phases enable each
 * individually.
 */
export type StepType =
  | 'narrate'
  | 'navigate'
  | 'scrollTo'
  | 'move'
  | 'hover'
  | 'click'
  | 'type'
  | 'press'
  | 'select'
  | 'waitFor'
  | 'wait';

/**
 * v2 step type names. Reserved-but-unauthored at Phase 1 — they exist
 * as shapes so the public type surface is stable across phases.
 */
export type StepTypeV2 =
  // Control flow
  | 'if' | 'unless' | 'when'
  | 'forEach' | 'repeat' | 'until'
  | 'try'
  | 'label' | 'goto' | 'skip' | 'break' | 'continue'
  // Variables / capture
  | 'let' | 'set' | 'capture'
  // Macros / abort
  | 'call' | 'abort'
  // Assertions
  | 'expect'
  // New action verbs
  | 'drag' | 'check' | 'uncheck' | 'pickDate'
  | 'copy' | 'paste'
  | 'waitForRequest' | 'waitForIdle'
  // Visual / annotation
  | 'highlight' | 'annotate' | 'multi-spot' | 'zoom' | 'pan'
  // Metadata-only markers (no-op at runtime; the player reads them for UI)
  | 'chapter';

/**
 * Common step header. Every Step — v1 or v2 — extends this.
 *
 * - `id` is optional but, when supplied, must be a stable kebab-snake
 *   identifier the parser will use to key locale overrides. The parser
 *   also assigns a deterministic `stableId` to every step regardless of
 *   author intent so v2 locale overlays can target steps by id.
 * - `retry` / `backoffMs` are honored by the runtime lifecycle wrapper.
 * - `precondition` / `postcondition` are evaluated as expressions
 *   before/after the step body.
 */
export interface BaseStep {
  id?: string;
  /**
   * Deterministic id derived from the step's position in the tour AST.
   * Always present after parsing; treated as opaque by consumers.
   * v1 tours receive auto-ids too so a single overlay format works
   * across versions.
   */
  stableId?: string;
  narrate?: string;
  /**
   * Compiled `${…}` template for {@link narrate}. v2 only. Present
   * when `narrate` contains interpolation markers; the runtime
   * evaluates this against the current scope to produce the actual
   * narration string. v1 tours and v2 tours without interpolation
   * leave this undefined and the runtime falls back to {@link narrate}.
   */
  narrateTpl?: Expr;
  execute?: boolean;
  timeoutMs?: number;
  settleMs?: number;
  retry?: number;
  backoffMs?: number;
  precondition?: Expr;
  postcondition?: Expr;
}

export interface NarrateStep extends BaseStep {
  type: 'narrate';
  text: string;
  /** Compiled `${…}` template for {@link text}. See {@link BaseStep.narrateTpl}. */
  textTpl?: Expr;
}

export interface NavigateStep extends BaseStep {
  type: 'navigate';
  to: string;
}

export interface ScrollToStep extends BaseStep {
  type: 'scrollTo';
  locator: Locator;
}

export interface MoveStep extends BaseStep {
  type: 'move';
  locator: Locator;
}

export interface HoverStep extends BaseStep {
  type: 'hover';
  locator: Locator;
}

export interface ClickStep extends BaseStep {
  type: 'click';
  locator: Locator;
}

export interface TypeStep extends BaseStep {
  type: 'type';
  locator: Locator;
  text: string;
  /** Compiled `${…}` template for {@link text}. See {@link BaseStep.narrateTpl}. */
  textTpl?: Expr;
  perCharMs?: number;
  clear?: boolean;
}

export interface PressStep extends BaseStep {
  type: 'press';
  key: string;
  locator?: Locator;
}

export interface SelectStep extends BaseStep {
  type: 'select';
  locator: Locator;
  value?: string;
  selectOption?: string;
}

export interface WaitForStep extends BaseStep {
  type: 'waitFor';
  locator: Locator;
}

export interface WaitStep extends BaseStep {
  type: 'wait';
  ms: number;
}

/* ─────────────────────────── v2 supporting types ─────────────────── */

/**
 * Parameter declaration on a tour or macro. Values are supplied at
 * invocation; defaults apply when omitted.
 */
export interface ParamDecl {
  readonly name: string;
  readonly type: 'string' | 'number' | 'bool' | { readonly enum: ReadonlyArray<string> };
  readonly required?: boolean;
  readonly default?: string | number | boolean;
}

/**
 * Header-level gating clause. The runtime evaluates these before the
 * tour starts and either runs the body, hides the tour, or surfaces a
 * "you don't have access" handoff depending on the kind.
 */
export interface Requirement {
  readonly kind: 'role' | 'permission' | 'flag' | 'plan' | 'env';
  readonly value: string;
}

/**
 * Per-axis tenant / plugin / role inclusion or exclusion list. Empty
 * arrays mean "no constraint on this axis".
 */
export interface ScopeFilter {
  readonly tenant?: ReadonlyArray<string>;
  readonly plugin?: ReadonlyArray<string>;
  readonly role?: ReadonlyArray<string>;
}

/**
 * Macro definition. Macros may be inline in the tour or imported from a
 * `.etlm` library file via `include`. Bodies are full step lists and
 * may themselves contain control flow / nested macro calls.
 */
export interface MacroDef {
  readonly name: string;
  readonly params: ReadonlyArray<ParamDecl>;
  readonly body: ReadonlyArray<Step>;
  /** Source path the macro was loaded from, if it came from a library file. */
  readonly source?: string;
}

/**
 * Assertion shape consumed by the `expect` step. Discriminated by
 * {@link Assertion.kind}; only the fields meaningful for that kind are
 * populated.
 */
export interface Assertion {
  readonly kind:
    | 'visible' | 'not-visible'
    | 'exists' | 'not-exists'
    | 'enabled' | 'disabled'
    | 'text' | 'value' | 'count' | 'expr';
  readonly locator?: Locator;
  readonly op?:
    | 'equals' | 'contains' | 'matches'
    | '==' | '!=' | '<' | '<=' | '>' | '>=';
  readonly expected?: string | number | { readonly regex: string; readonly flags: string };
  /** Only populated when {@link Assertion.kind} === 'expr'. */
  readonly expr?: Expr;
  readonly softFail?: boolean;
}

/**
 * Composable locator AST used by the v2 resolver. The existing
 * {@link Locator} stays as a single-step locator; chains, spatial
 * relations, and locator-variable references add new node kinds. The
 * v1 `within:` modifier validates into a chain transparently.
 */
export type LocatorExpr =
  | { readonly kind: 'step'; readonly locator: Locator }
  | { readonly kind: 'chain'; readonly steps: ReadonlyArray<LocatorExpr> }
  | { readonly kind: 'containing'; readonly outer: LocatorExpr; readonly inner: LocatorExpr }
  | {
      readonly kind: 'spatial';
      readonly relation: 'right-of' | 'left-of' | 'above' | 'below' | 'near';
      readonly pivot: LocatorExpr;
      readonly candidate: LocatorExpr;
      readonly distance?: number;
    }
  | { readonly kind: 'var'; readonly name: string };

/* ─────────────────────────── v2 step shapes (typed-only Phase 1) ─── */

// Control flow
export interface IfStep extends BaseStep {
  type: 'if';
  branches: ReadonlyArray<{ readonly test: Expr; readonly body: ReadonlyArray<Step> }>;
  otherwise?: ReadonlyArray<Step>;
}
export interface UnlessStep extends BaseStep { type: 'unless'; test: Expr; body: ReadonlyArray<Step>; }
export interface WhenStep   extends BaseStep { type: 'when';   test: Expr; body: ReadonlyArray<Step>; }
export interface ForEachStep extends BaseStep {
  type: 'forEach';
  varName: string;
  source: Expr | { readonly all: LocatorExpr };
  body: ReadonlyArray<Step>;
}
export interface RepeatStep extends BaseStep { type: 'repeat'; count: number; body: ReadonlyArray<Step>; }
export interface UntilStep  extends BaseStep { type: 'until';  test: Expr; body: ReadonlyArray<Step>; }
export interface TryStep    extends BaseStep {
  type: 'try';
  body: ReadonlyArray<Step>;
  catchVar?: string;
  catchBody?: ReadonlyArray<Step>;
  finallyBody?: ReadonlyArray<Step>;
}
export interface LabelStep    extends BaseStep { type: 'label'; name: string; }
export interface GotoStep     extends BaseStep { type: 'goto';  label: string; }
export interface SkipStep     extends BaseStep { type: 'skip'; }
export interface BreakStep    extends BaseStep { type: 'break'; }
export interface ContinueStep extends BaseStep { type: 'continue'; }

// Variables / capture
export interface LetStep extends BaseStep {
  type: 'let';
  name: string;
  /** A `let` may bind either a value (Expr) or a locator-AST. */
  value: Expr | { readonly locator: LocatorExpr };
}
export interface SetStep extends BaseStep { type: 'set'; name: string; value: Expr; }
export interface CaptureStep extends BaseStep {
  type: 'capture';
  locator: Locator;
  as: string;
  what?: 'text' | 'value' | 'count';
}

// Macros / abort
export interface CallStep extends BaseStep {
  type: 'call';
  macro: string;
  args: Readonly<Record<string, Expr>>;
}
export interface AbortStep extends BaseStep { type: 'abort'; reason: string; helpUrl?: string; }

// Assertions
export interface ExpectStep extends BaseStep { type: 'expect'; assertion: Assertion; }

// New action verbs
export interface DragStep     extends BaseStep { type: 'drag'; from: Locator; to: Locator; }
export interface CheckStep    extends BaseStep { type: 'check';   locator: Locator; }
export interface UncheckStep  extends BaseStep { type: 'uncheck'; locator: Locator; }
export interface PickDateStep extends BaseStep {
  type: 'pickDate'; locator: Locator; date: string; format?: string; mode?: 'native' | 'custom';
}
export interface CopyStep  extends BaseStep { type: 'copy';  locator: Locator; into: string; }
export interface PasteStep extends BaseStep { type: 'paste'; locator: Locator; from: string; }
export interface WaitForRequestStep extends BaseStep {
  type: 'waitForRequest'; urlPattern: string; status?: number; method?: string;
}
export interface WaitForIdleStep extends BaseStep { type: 'waitForIdle'; }

// Visual / annotation
export interface HighlightStep extends BaseStep { type: 'highlight'; locator: Locator; }
export interface AnnotateStep extends BaseStep {
  type: 'annotate';
  locator: Locator;
  text: string;
  sticky?: boolean;
  placement?: 'right-of' | 'left-of' | 'above' | 'below' | 'near';
}
export interface MultiSpotStep extends BaseStep { type: 'multi-spot'; locators: ReadonlyArray<Locator>; }
export interface ZoomStep extends BaseStep { type: 'zoom'; locator?: Locator; factor?: number; }
export interface PanStep  extends BaseStep { type: 'pan';  dx?: number; dy?: number; }

/**
 * Metadata-only chapter marker. Has no runtime side effect beyond the
 * player recording the title as the "current chapter" so the spotlight
 * card can render which phase of the tour the user is in. Counts as a
 * step in the timeline but is skipped immediately by the runner with no
 * settle pause.
 */
export interface ChapterStep extends BaseStep { type: 'chapter'; title: string; }

export type Step =
  // v1 (admitted by the validator today)
  | NarrateStep
  | NavigateStep
  | ScrollToStep
  | MoveStep
  | HoverStep
  | ClickStep
  | TypeStep
  | PressStep
  | SelectStep
  | WaitForStep
  | WaitStep
  // v2 (typed only — validator rejects until each is enabled)
  | IfStep | UnlessStep | WhenStep
  | ForEachStep | RepeatStep | UntilStep
  | TryStep
  | LabelStep | GotoStep | SkipStep | BreakStep | ContinueStep
  | LetStep | SetStep | CaptureStep
  | CallStep | AbortStep
  | ExpectStep
  | DragStep | CheckStep | UncheckStep | PickDateStep
  | CopyStep | PasteStep
  | WaitForRequestStep | WaitForIdleStep
  | HighlightStep | AnnotateStep | MultiSpotStep | ZoomStep | PanStep
  | ChapterStep;

export interface TourMatch {
  route?: string;
  routePattern?: string;
}

export type DemoMode = 'dry-run' | 'sandbox';

export interface TourPace {
  cursorMs?: number;
  perCharMs?: number;
  settleMs?: number;
  narrateMs?: number;
}

export interface TourStepLocale {
  narrate?: string;
}

export interface TourLocaleOverrides {
  title?: string;
  summary?: string;
  steps?: (TourStepLocale | undefined)[];
}

export type EtlVersion = 1 | 3;

export interface Tour {
  id: string;
  pluginId: string;
  title: string;
  summary?: string;
  guidePath?: string;
  match?: TourMatch;
  demoMode?: DemoMode;
  pace?: TourPace;
  steps: Step[];
  locales?: Record<string, TourLocaleOverrides>;
  /**
   * ETL grammar version this tour was authored against. Default `1`.
   * Tours that use v2 constructs (params, requires, control flow,
   * macros, expect, etc.) must declare `etl-version 2` in the header.
   */
  etlVersion?: EtlVersion;
  /** Parameters bound at tour invocation. v2 only. */
  params?: ReadonlyArray<ParamDecl>;
  /** Header-level gating clauses. v2 only. */
  requires?: ReadonlyArray<Requirement>;
  /** Tenant / plugin / role inclusion list. v2 only. */
  onlyFor?: ScopeFilter;
  /** Tenant / plugin / role exclusion list. v2 only. */
  notFor?: ScopeFilter;
  /** Tour ids that must be completed before this one is suggested. v2 only. */
  prerequisites?: ReadonlyArray<string>;
  /** Macro definitions in scope (own + included). v2 only. */
  macros?: Readonly<Record<string, MacroDef>>;
  /**
   * Library file paths the tour pulls macros from. v2 only. The
   * portable parser only records what was authored; the host catalog
   * is responsible for loading each path and merging its `macros` into
   * the tour's `macros` map.
   */
  includes?: ReadonlyArray<string>;
  /** True when this file is a `library` macro pack and exposes no body steps. */
  isLibrary?: boolean;
  /** Opt into strict expression-evaluator semantics. v2 only. */
  strict?: boolean;
  /**
   * How the runtime should handle a step that can't resolve its locator
   * (ActionAbortError) or whose assertion hard-fails. `abort` (default)
   * surfaces the failure and stops the tour. `skip` narrates "skipped …"
   * and advances to the next step — appropriate for best-effort
   * onboarding tours that walk transient UI.
   */
  onError?: 'skip' | 'abort';
}

export class TourValidationError extends Error {
  readonly path: string;
  constructor(message: string, path: string) {
    super(`[etl-tour] tour validation failed at ${path || '<root>'}: ${message}`);
    this.path = path;
    this.name = 'TourValidationError';
  }
}

const STEP_TYPES: ReadonlySet<StepType> = new Set<StepType>([
  'narrate', 'navigate', 'scrollTo', 'move', 'hover', 'click', 'type', 'press', 'select', 'waitFor', 'wait',
]);

/**
 * v2 step types the validator currently accepts. The set grows phase by
 * phase — Phase 2 admits `expect`; Phase 3 adds control flow, decls,
 * and loop-control verbs. Anything in {@link StepTypeV2} but not in
 * this set is rejected with `unknown step type` even when constructed
 * by hand.
 */
const STEP_TYPES_V2: ReadonlySet<StepTypeV2> = new Set<StepTypeV2>([
  'expect',
  // Phase 3 — control flow
  'if', 'forEach', 'try',
  // Phase 3 — decls
  'let', 'set', 'capture',
  // Phase 3 — loop control
  'break', 'continue', 'skip',
  // Phase 5 — macro invocation
  'call',
  // Phase 6 — visual + interaction verbs
  'highlight', 'check', 'uncheck', 'drag',
  'multi-spot', 'annotate',
  // Metadata markers
  'chapter',
]);

const ASSERT_KINDS: ReadonlySet<Assertion['kind']> = new Set<Assertion['kind']>([
  'visible', 'not-visible', 'exists', 'not-exists',
  'enabled', 'disabled',
  'text', 'value', 'count', 'expr',
]);

const ASSERT_OPS: ReadonlySet<Required<Assertion>['op']> = new Set<Required<Assertion>['op']>([
  'equals', 'contains', 'matches',
  '==', '!=', '<', '<=', '>', '>=',
]);

const ROLE_NAMES: ReadonlySet<LocatorRoleName> = new Set<LocatorRoleName>([
  'button', 'link', 'textbox', 'searchbox', 'combobox', 'checkbox', 'radio',
  'switch', 'tab', 'menuitem', 'option', 'heading',
  'table', 'grid', 'row', 'cell', 'columnheader', 'rowheader',
  'list', 'listitem', 'dialog', 'alertdialog', 'alert', 'status',
  'img', 'spinbutton',
]);

/**
 * The runtime executor refuses to dispatch a click whose accessible name
 * matches this pattern unless `demoMode` is `'sandbox'`. Exported so
 * higher layers (catalog UI, agent client) can audit tours offline.
 */
export const DESTRUCTIVE_NAME_PATTERN =
  /^\s*(?:🗑\s*)?(?:delete|remove|revoke|reset password|disable|destroy|wipe|terminate|deactivate)\b/i;

export interface TourLogEntry {
  id: string;
  message: string;
  stepType: string;
  stepIndex: number;
  terminalStatus?: 'finished' | 'handoff' | 'aborted';
}

/* ─────────────────────────── primitives ───────────────────────────── */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireString(value: unknown, path: string, max: number): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new TourValidationError('expected non-empty string', path);
  }
  if (value.length > max) {
    throw new TourValidationError(`string exceeds limit of ${max} chars`, path);
  }
  return value;
}

function optionalString(value: unknown, path: string, max: number): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'string') {
    throw new TourValidationError('expected string', path);
  }
  if (value.length > max) {
    throw new TourValidationError(`string exceeds limit of ${max} chars`, path);
  }
  return value;
}

function optionalBoolean(value: unknown, path: string): boolean | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'boolean') {
    throw new TourValidationError('expected boolean', path);
  }
  return value;
}

function optionalNumber(value: unknown, path: string, max?: number): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TourValidationError('expected finite number', path);
  }
  if (max !== undefined && (value < 0 || value > max)) {
    throw new TourValidationError(`number must be in [0, ${max}]`, path);
  }
  return value;
}

/* ─────────────────────────── locator ──────────────────────────────── */

function validateLocator(value: unknown, path: string, depth: number, limits: EtlLimits): Locator {
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected object', path);
  }
  if (depth > limits.maxLocatorDepth) {
    throw new TourValidationError(
      `nested "within" depth exceeds ${limits.maxLocatorDepth}`,
      path,
    );
  }

  const locator: Locator = {};
  const idMax = limits.maxIdentifierLength;
  const strMax = limits.maxStringLength;

  if ('testid' in value) locator.testid = optionalString(value.testid, `${path}.testid`, idMax);
  if ('role' in value) {
    const role = optionalString(value.role, `${path}.role`, idMax);
    if (role !== undefined) {
      if (!ROLE_NAMES.has(role as LocatorRoleName)) {
        throw new TourValidationError(`unknown role "${role}"`, `${path}.role`);
      }
      locator.role = role as LocatorRoleName;
    }
  }
  if ('name' in value) locator.name = optionalString(value.name, `${path}.name`, strMax);
  if ('label' in value) locator.label = optionalString(value.label, `${path}.label`, strMax);
  if ('text' in value) locator.text = optionalString(value.text, `${path}.text`, strMax);
  if ('selector' in value) {
    const sel = optionalString(value.selector, `${path}.selector`, limits.maxSelectorLength);
    if (sel !== undefined) {
      if (!SAFE_SELECTOR_PATTERN.test(sel)) {
        throw new TourValidationError(
          'selector contains characters outside the safe allow-list',
          `${path}.selector`,
        );
      }
      locator.selector = sel;
    }
  }
  if ('exact' in value) locator.exact = optionalBoolean(value.exact, `${path}.exact`);
  if ('nth' in value) locator.nth = optionalNumber(value.nth, `${path}.nth`);
  if ('within' in value && value.within !== undefined) {
    locator.within = validateLocator(value.within, `${path}.within`, depth + 1, limits);
  }
  if ('varRef' in value && value.varRef !== undefined) {
    const v = optionalString(value.varRef, `${path}.varRef`, idMax);
    if (v !== undefined) locator.varRef = v;
  }
  if ('alias' in value && value.alias !== undefined) {
    if (!isPlainObject(value.alias)) {
      throw new TourValidationError('alias must be { plugin, name }', `${path}.alias`);
    }
    const aliasObj = value.alias as Record<string, unknown>;
    // `plugin` may be the empty string (means "active plugin"); name
    // must be a non-empty kebab-and-dot identifier. The full check on
    // the name pattern lives in the lexer; here we just bound length.
    const plugin = typeof aliasObj.plugin === 'string'
      ? aliasObj.plugin
      : (() => { throw new TourValidationError('alias.plugin must be a string', `${path}.alias.plugin`); })();
    if (plugin.length > idMax) {
      throw new TourValidationError(`alias.plugin exceeds ${idMax} chars`, `${path}.alias.plugin`);
    }
    const name = requireString(aliasObj.name, `${path}.alias.name`, idMax);
    locator.alias = { plugin, name };
  }

  const hasAnyStrategy =
    locator.testid !== undefined ||
    locator.role !== undefined ||
    locator.label !== undefined ||
    locator.text !== undefined ||
    locator.selector !== undefined ||
    locator.varRef !== undefined ||
    locator.alias !== undefined;
  if (!hasAnyStrategy) {
    throw new TourValidationError(
      'locator must specify at least one of: testid, role, label, text, selector, varRef, alias',
      path,
    );
  }
  return locator;
}

/* ─────────────────────────── assertion (v2) ──────────────────────── */

function validateAssertion(value: unknown, path: string, limits: EtlLimits): Assertion {
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected object', path);
  }
  const kindRaw = requireString(value.kind, `${path}.kind`, limits.maxIdentifierLength);
  if (!ASSERT_KINDS.has(kindRaw as Assertion['kind'])) {
    throw new TourValidationError(`unknown assertion kind "${kindRaw}"`, `${path}.kind`);
  }
  const kind = kindRaw as Assertion['kind'];

  // Pure-locator assertions: locator required, no op/expected.
  const pureLocator: ReadonlySet<Assertion['kind']> = new Set([
    'visible', 'not-visible', 'exists', 'not-exists', 'enabled', 'disabled',
  ]);

  let locator: Locator | undefined;
  if ('locator' in value && value.locator !== undefined) {
    locator = validateLocator(value.locator, `${path}.locator`, 0, limits);
  }

  if (pureLocator.has(kind)) {
    if (!locator) {
      throw new TourValidationError(
        `assertion kind "${kind}" requires a locator`,
        path,
      );
    }
    const out: Assertion = { kind, locator };
    if ('softFail' in value) {
      const sf = optionalBoolean(value.softFail, `${path}.softFail`);
      if (sf !== undefined) (out as { softFail?: boolean }).softFail = sf;
    }
    return out;
  }

  if (kind === 'text' || kind === 'value') {
    if (!locator) {
      throw new TourValidationError(
        `assertion kind "${kind}" requires a locator`,
        path,
      );
    }
    const opRaw = requireString(value.op, `${path}.op`, limits.maxIdentifierLength);
    if (!ASSERT_OPS.has(opRaw as Required<Assertion>['op'])) {
      throw new TourValidationError(
        `unknown op "${opRaw}" for assertion "${kind}"`,
        `${path}.op`,
      );
    }
    if (!(opRaw === 'equals' || opRaw === 'contains' || opRaw === 'matches')) {
      throw new TourValidationError(
        `op "${opRaw}" is not valid for assertion "${kind}" — use equals|contains|matches`,
        `${path}.op`,
      );
    }
    const expected = value.expected;
    if (typeof expected !== 'string') {
      // Regex object form is reserved for a later phase that wires in
      // regex literals — Phase 2 only accepts string `equals`/`contains`.
      throw new TourValidationError(
        `expected value for "${kind}" must be a string`,
        `${path}.expected`,
      );
    }
    if (expected.length > limits.maxStringLength) {
      throw new TourValidationError(
        `expected exceeds ${limits.maxStringLength} chars`,
        `${path}.expected`,
      );
    }
    const out: Assertion = { kind, locator, op: opRaw as Assertion['op'], expected };
    if ('softFail' in value) {
      const sf = optionalBoolean(value.softFail, `${path}.softFail`);
      if (sf !== undefined) (out as { softFail?: boolean }).softFail = sf;
    }
    return out;
  }

  if (kind === 'count') {
    if (!locator) {
      throw new TourValidationError('assertion kind "count" requires a locator', path);
    }
    const opRaw = requireString(value.op, `${path}.op`, limits.maxIdentifierLength);
    const numericOps: ReadonlySet<string> = new Set(['==', '!=', '<', '<=', '>', '>=']);
    if (!numericOps.has(opRaw)) {
      throw new TourValidationError(
        `op "${opRaw}" is not valid for assertion "count" — use one of: ${[...numericOps].join(' ')}`,
        `${path}.op`,
      );
    }
    if (typeof value.expected !== 'number' || !Number.isFinite(value.expected)) {
      throw new TourValidationError(
        'expected value for "count" must be a finite number',
        `${path}.expected`,
      );
    }
    const out: Assertion = { kind, locator, op: opRaw as Assertion['op'], expected: value.expected };
    if ('softFail' in value) {
      const sf = optionalBoolean(value.softFail, `${path}.softFail`);
      if (sf !== undefined) (out as { softFail?: boolean }).softFail = sf;
    }
    return out;
  }

  // kind === 'expr' — reserved for a later phase that wires Expr in
  // assertions. Phase 2 doesn't accept it from JSON.
  throw new TourValidationError(
    `assertion kind "${kind}" is reserved for a later ETL phase`,
    `${path}.kind`,
  );
}

/* ─────────────────────── v2 header construct validators ──────────── */

const SCALAR_PARAM_TYPES: ReadonlySet<string> = new Set(['string', 'number', 'bool']);

function validateParamDecls(value: unknown, path: string, limits: EtlLimits): ParamDecl[] {
  if (!Array.isArray(value)) {
    throw new TourValidationError('expected array', path);
  }
  if (value.length > limits.maxParams) {
    throw new TourValidationError(
      `params length (${value.length}) exceeds limit ${limits.maxParams}`,
      path,
    );
  }
  const seen = new Set<string>();
  return value.map((p, i) => {
    if (!isPlainObject(p)) {
      throw new TourValidationError('expected object', `${path}[${i}]`);
    }
    const name = requireString(p.name, `${path}[${i}].name`, limits.maxIdentifierLength);
    if (seen.has(name)) {
      throw new TourValidationError(`duplicate param "${name}"`, `${path}[${i}].name`);
    }
    seen.add(name);
    let type: ParamDecl['type'];
    if (typeof p.type === 'string') {
      if (!SCALAR_PARAM_TYPES.has(p.type)) {
        throw new TourValidationError(
          `unknown param type "${p.type}" — use string|number|bool|enum`,
          `${path}[${i}].type`,
        );
      }
      type = p.type as 'string' | 'number' | 'bool';
    } else if (isPlainObject(p.type) && Array.isArray((p.type as { enum?: unknown }).enum)) {
      const members = (p.type as { enum: unknown[] }).enum;
      if (members.length === 0) {
        throw new TourValidationError('enum members must be non-empty', `${path}[${i}].type.enum`);
      }
      type = { enum: members.map((m, j) => requireString(m, `${path}[${i}].type.enum[${j}]`, limits.maxIdentifierLength)) };
    } else {
      throw new TourValidationError(
        'param type must be a string or { enum: [...] }',
        `${path}[${i}].type`,
      );
    }
    const out: ParamDecl = { name, type };
    if (p.required !== undefined) {
      const r = optionalBoolean(p.required, `${path}[${i}].required`);
      if (r === true) (out as { required?: boolean }).required = true;
    }
    if (p.default !== undefined) {
      if (typeof p.default !== 'string' && typeof p.default !== 'number' && typeof p.default !== 'boolean') {
        throw new TourValidationError(
          'default must be string | number | boolean',
          `${path}[${i}].default`,
        );
      }
      (out as { default?: string | number | boolean }).default = p.default;
    }
    return out;
  });
}

function validateRequirements(value: unknown, path: string, limits: EtlLimits): Requirement[] {
  if (!Array.isArray(value)) {
    throw new TourValidationError('expected array', path);
  }
  return value.map((r, i) => {
    if (!isPlainObject(r)) {
      throw new TourValidationError('expected object', `${path}[${i}]`);
    }
    const kind = requireString(r.kind, `${path}[${i}].kind`, limits.maxIdentifierLength);
    if (!['role', 'permission', 'flag', 'plan', 'env'].includes(kind)) {
      throw new TourValidationError(
        `unknown requirement kind "${kind}"`,
        `${path}[${i}].kind`,
      );
    }
    const v = requireString(r.value, `${path}[${i}].value`, limits.maxStringLength);
    return { kind: kind as Requirement['kind'], value: v };
  });
}

function validateScopeFilter(value: unknown, path: string, limits: EtlLimits): ScopeFilter {
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected object', path);
  }
  const out: { tenant?: string[]; plugin?: string[]; role?: string[] } = {};
  for (const axis of ['tenant', 'plugin', 'role'] as const) {
    if (axis in value && value[axis] !== undefined) {
      const arr = value[axis];
      if (!Array.isArray(arr)) {
        throw new TourValidationError('expected array of strings', `${path}.${axis}`);
      }
      out[axis] = arr.map((s, i) => requireString(s, `${path}.${axis}[${i}]`, limits.maxIdentifierLength));
    }
  }
  return out;
}

/* ─────────────────────── v2 control-flow validators ──────────────── */

const EXPR_KINDS: ReadonlySet<string> = new Set([
  'lit', 'ident', 'member', 'index', 'unary', 'binary',
  'call', 'tpl', 'regex', 'locator',
]);

/**
 * Lightweight structural Expr validator. Doesn't typecheck the
 * underlying expression — the parser already produces well-formed
 * trees. This is the JSON-input safety net: catches obvious shape
 * errors before the runtime evaluates the AST.
 */
function validateExpr(value: unknown, path: string, depth: number, limits: EtlLimits): void {
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected expression object', path);
  }
  const kind = value.kind;
  if (typeof kind !== 'string' || !EXPR_KINDS.has(kind)) {
    throw new TourValidationError(`unknown expression kind "${String(kind)}"`, `${path}.kind`);
  }
  if (depth > limits.maxExprDepth) {
    throw new TourValidationError(
      `expression nesting exceeds ${limits.maxExprDepth}`,
      path,
    );
  }
  // Recurse into structural children only — leaves don't need walking.
  switch (kind) {
    case 'unary':
      validateExpr((value as { arg: unknown }).arg, `${path}.arg`, depth + 1, limits);
      break;
    case 'binary':
      validateExpr((value as { left: unknown }).left, `${path}.left`, depth + 1, limits);
      validateExpr((value as { right: unknown }).right, `${path}.right`, depth + 1, limits);
      break;
    case 'member':
    case 'index': {
      const v = value as { obj: unknown; key?: unknown };
      validateExpr(v.obj, `${path}.obj`, depth + 1, limits);
      if (kind === 'index' && v.key !== undefined) {
        validateExpr(v.key, `${path}.key`, depth + 1, limits);
      }
      break;
    }
    case 'call': {
      const args = (value as { args: unknown }).args;
      if (!Array.isArray(args)) {
        throw new TourValidationError('expected array', `${path}.args`);
      }
      args.forEach((a, i) => validateExpr(a, `${path}.args[${i}]`, depth + 1, limits));
      break;
    }
    case 'tpl': {
      const parts = (value as { parts: unknown }).parts;
      if (!Array.isArray(parts)) {
        throw new TourValidationError('expected array', `${path}.parts`);
      }
      parts.forEach((p, i) => {
        if (typeof p !== 'string') validateExpr(p, `${path}.parts[${i}]`, depth + 1, limits);
      });
      break;
    }
  }
}

/**
 * Recursive-but-bounded validator for a child step array (`if.body`,
 * `forEach.body`, etc.). Bounds are inherited from {@link EtlLimits}.
 */
function validateStepArray(value: unknown, path: string, limits: EtlLimits): Step[] {
  if (!Array.isArray(value)) {
    throw new TourValidationError('expected array of steps', path);
  }
  return value.map((s, i) => validateStep(s, `${path}[${i}]`, limits));
}

function validateIfStep(value: Record<string, unknown>, base: BaseStep, path: string, limits: EtlLimits): Step {
  const branches = value.branches;
  if (!Array.isArray(branches) || branches.length === 0) {
    throw new TourValidationError('"if" step requires a non-empty branches array', `${path}.branches`);
  }
  const validatedBranches = branches.map((b, i) => {
    if (!isPlainObject(b)) {
      throw new TourValidationError('expected branch object', `${path}.branches[${i}]`);
    }
    validateExpr(b.test, `${path}.branches[${i}].test`, 0, limits);
    return {
      test: b.test as Expr,
      body: validateStepArray(b.body, `${path}.branches[${i}].body`, limits),
    };
  });
  const otherwise = value.otherwise === undefined
    ? undefined
    : validateStepArray(value.otherwise, `${path}.otherwise`, limits);
  return {
    ...base,
    type: 'if',
    branches: validatedBranches,
    ...(otherwise !== undefined && { otherwise }),
  };
}

function validateForEachStep(value: Record<string, unknown>, base: BaseStep, path: string, limits: EtlLimits): Step {
  const varName = requireString(value.varName, `${path}.varName`, limits.maxIdentifierLength);
  const source = value.source;
  if (isPlainObject(source) && 'all' in source) {
    // { all: LocatorExpr } — locator-list iteration. For Phase 3 we
    // don't yet wire LocatorExpr resolution; reject so the runtime
    // never sees an unimplemented form.
    throw new TourValidationError(
      'forEach over a locator-list is reserved for a later phase',
      `${path}.source`,
    );
  }
  validateExpr(source, `${path}.source`, 0, limits);
  return {
    ...base,
    type: 'forEach',
    varName,
    source: source as Expr,
    body: validateStepArray(value.body, `${path}.body`, limits),
  };
}

function validateTryStep(value: Record<string, unknown>, base: BaseStep, path: string, limits: EtlLimits): Step {
  const out: Step = {
    ...base,
    type: 'try',
    body: validateStepArray(value.body, `${path}.body`, limits),
  };
  if (value.catchVar !== undefined) {
    (out as { catchVar?: string }).catchVar = optionalString(value.catchVar, `${path}.catchVar`, limits.maxIdentifierLength);
  }
  if (value.catchBody !== undefined) {
    (out as { catchBody?: Step[] }).catchBody = validateStepArray(value.catchBody, `${path}.catchBody`, limits);
  }
  if (value.finallyBody !== undefined) {
    (out as { finallyBody?: Step[] }).finallyBody = validateStepArray(value.finallyBody, `${path}.finallyBody`, limits);
  }
  return out;
}

function validateLetStep(value: Record<string, unknown>, base: BaseStep, path: string, limits: EtlLimits): Step {
  const name = requireString(value.name, `${path}.name`, limits.maxIdentifierLength);
  const v = value.value;
  if (isPlainObject(v) && 'locator' in v) {
    // locator-binding form — not wired to runtime yet (Phase 5
    // introduces locator AST resolution). Validate shape only.
    if (!isPlainObject((v as { locator: unknown }).locator)) {
      throw new TourValidationError('expected locator AST', `${path}.value.locator`);
    }
    return {
      ...base,
      type: 'let',
      name,
      value: { locator: (v as { locator: unknown }).locator } as never,
    };
  }
  validateExpr(v, `${path}.value`, 0, limits);
  return { ...base, type: 'let', name, value: v as Expr };
}

function validateSetStep(value: Record<string, unknown>, base: BaseStep, path: string, limits: EtlLimits): Step {
  const name = requireString(value.name, `${path}.name`, limits.maxIdentifierLength);
  validateExpr(value.value, `${path}.value`, 0, limits);
  return { ...base, type: 'set', name, value: value.value as Expr };
}

function validateCaptureWhat(value: unknown, path: string): 'text' | 'value' | 'count' {
  if (value !== 'text' && value !== 'value' && value !== 'count') {
    throw new TourValidationError(
      'capture "what" must be one of: text | value | count',
      path,
    );
  }
  return value;
}

function validateCallStep(value: Record<string, unknown>, base: BaseStep, path: string, limits: EtlLimits): Step {
  const macro = requireString(value.macro, `${path}.macro`, limits.maxIdentifierLength);
  const args: Record<string, Expr> = {};
  if (value.args !== undefined) {
    if (!isPlainObject(value.args)) {
      throw new TourValidationError('expected object', `${path}.args`);
    }
    for (const k of Object.keys(value.args)) {
      const a = (value.args as Record<string, unknown>)[k];
      validateExpr(a, `${path}.args.${k}`, 0, limits);
      args[k] = a as Expr;
    }
  }
  return { ...base, type: 'call', macro, args };
}

function validateMacroDefs(value: unknown, path: string, limits: EtlLimits): Record<string, MacroDef> {
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected object', path);
  }
  const out: Record<string, MacroDef> = {};
  const names = Object.keys(value);
  if (names.length > limits.maxMacros) {
    throw new TourValidationError(
      `macros count (${names.length}) exceeds limit ${limits.maxMacros}`,
      path,
    );
  }
  for (const name of names) {
    const m = (value as Record<string, unknown>)[name];
    if (!isPlainObject(m)) {
      throw new TourValidationError('expected macro object', `${path}.${name}`);
    }
    const params = m.params === undefined
      ? []
      : validateParamDecls(m.params, `${path}.${name}.params`, limits);
    const body = validateStepArray(m.body, `${path}.${name}.body`, limits);
    const declaredName = requireString(m.name, `${path}.${name}.name`, limits.maxIdentifierLength);
    if (declaredName !== name) {
      throw new TourValidationError(
        `macro key "${name}" does not match declared name "${declaredName}"`,
        `${path}.${name}.name`,
      );
    }
    out[name] = {
      name,
      params,
      body,
      ...(typeof m.source === 'string' && { source: m.source }),
    };
  }
  return out;
}

/* ─────────────────────────── steps ────────────────────────────────── */

function validateStep(value: unknown, path: string, limits: EtlLimits): Step {
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected object', path);
  }
  const type = requireString(value.type, `${path}.type`, limits.maxIdentifierLength);
  const isV1 = STEP_TYPES.has(type as StepType);
  const isV2 = STEP_TYPES_V2.has(type as StepTypeV2);
  if (!isV1 && !isV2) {
    throw new TourValidationError(`unknown step type "${type}"`, `${path}.type`);
  }

  const idMax = limits.maxIdentifierLength;
  const strMax = limits.maxStringLength;
  const msMax = limits.maxWaitMs;

  const base: BaseStep = {};
  if ('id' in value) base.id = optionalString(value.id, `${path}.id`, idMax);
  if ('stableId' in value) base.stableId = optionalString(value.stableId, `${path}.stableId`, idMax);
  if ('narrate' in value) base.narrate = optionalString(value.narrate, `${path}.narrate`, strMax);
  if ('execute' in value) base.execute = optionalBoolean(value.execute, `${path}.execute`);
  if ('timeoutMs' in value) base.timeoutMs = optionalNumber(value.timeoutMs, `${path}.timeoutMs`, msMax);
  if ('settleMs' in value) base.settleMs = optionalNumber(value.settleMs, `${path}.settleMs`, msMax);
  if ('retry' in value) {
    const r = optionalNumber(value.retry, `${path}.retry`, 16);
    if (r !== undefined) {
      if (!Number.isInteger(r)) {
        throw new TourValidationError('retry must be an integer', `${path}.retry`);
      }
      base.retry = r;
    }
  }
  if ('backoffMs' in value) base.backoffMs = optionalNumber(value.backoffMs, `${path}.backoffMs`, msMax);
  if ('narrateTpl' in value && value.narrateTpl !== undefined) {
    validateExpr(value.narrateTpl, `${path}.narrateTpl`, 0, limits);
    base.narrateTpl = value.narrateTpl as Expr;
  }

  const loc = (k: string = 'locator'): Locator =>
    validateLocator((value as Record<string, unknown>)[k], `${path}.${k}`, 0, limits);

  // v2 step kinds — dispatch first so the v1 switch below stays exhaustive.
  if (isV2) {
    switch (type as StepTypeV2) {
      case 'expect':
        return {
          ...base,
          type: 'expect',
          assertion: validateAssertion(value.assertion, `${path}.assertion`, limits),
        };
      case 'if':
        return validateIfStep(value, base, path, limits);
      case 'forEach':
        return validateForEachStep(value, base, path, limits);
      case 'try':
        return validateTryStep(value, base, path, limits);
      case 'let':
        return validateLetStep(value, base, path, limits);
      case 'set':
        return validateSetStep(value, base, path, limits);
      case 'capture':
        return {
          ...base,
          type: 'capture',
          locator: validateLocator(value.locator, `${path}.locator`, 0, limits),
          as: requireString(value.as, `${path}.as`, idMax),
          ...(value.what !== undefined && { what: validateCaptureWhat(value.what, `${path}.what`) }),
        };
      case 'break':
      case 'continue':
      case 'skip':
        return { ...base, type: type as 'break' | 'continue' | 'skip' };
      case 'call':
        return validateCallStep(value, base, path, limits);
      case 'highlight':
      case 'check':
      case 'uncheck':
        return {
          ...base,
          type: type as 'highlight' | 'check' | 'uncheck',
          locator: validateLocator(value.locator, `${path}.locator`, 0, limits),
        };
      case 'drag':
        return {
          ...base,
          type: 'drag',
          from: validateLocator(value.from, `${path}.from`, 0, limits),
          to:   validateLocator(value.to,   `${path}.to`,   0, limits),
        };
      case 'multi-spot': {
        if (!Array.isArray(value.locators) || value.locators.length === 0) {
          throw new TourValidationError(
            'multi-spot requires a non-empty "locators" array',
            `${path}.locators`,
          );
        }
        return {
          ...base,
          type: 'multi-spot',
          locators: value.locators.map((l, i) =>
            validateLocator(l, `${path}.locators[${i}]`, 0, limits),
          ),
        };
      }
      case 'chapter': {
        return {
          ...base,
          type: 'chapter',
          title: requireString(value.title, `${path}.title`, strMax),
        };
      }
      case 'annotate': {
        const placement = value.placement;
        if (placement !== undefined && !['right-of', 'left-of', 'above', 'below', 'near'].includes(placement as string)) {
          throw new TourValidationError(
            'placement must be one of: right-of, left-of, above, below, near',
            `${path}.placement`,
          );
        }
        const out = {
          ...base,
          type: 'annotate' as const,
          locator: validateLocator(value.locator, `${path}.locator`, 0, limits),
          text: requireString(value.text, `${path}.text`, strMax),
        };
        if (placement !== undefined) {
          (out as { placement?: 'right-of' | 'left-of' | 'above' | 'below' | 'near' })
            .placement = placement as 'right-of' | 'left-of' | 'above' | 'below' | 'near';
        }
        if ('sticky' in value) {
          const s = optionalBoolean(value.sticky, `${path}.sticky`);
          if (s !== undefined) (out as { sticky?: boolean }).sticky = s;
        }
        return out;
      }
      // Other v2 kinds are reserved (typed but not yet validatable) — see
      // {@link STEP_TYPES_V2}.
      default:
        throw new TourValidationError(`unknown step type "${type}"`, `${path}.type`);
    }
  }

  switch (type as StepType) {
    case 'narrate': {
      const narrateStep: NarrateStep = {
        ...base,
        type: 'narrate',
        text: requireString(value.text, `${path}.text`, strMax),
      };
      if ('textTpl' in value && value.textTpl !== undefined) {
        validateExpr(value.textTpl, `${path}.textTpl`, 0, limits);
        narrateStep.textTpl = value.textTpl as Expr;
      }
      return narrateStep;
    }
    case 'navigate':
      return { ...base, type: 'navigate', to: requireString(value.to, `${path}.to`, strMax) };
    case 'scrollTo':
      return { ...base, type: 'scrollTo', locator: loc() };
    case 'move':
      return { ...base, type: 'move', locator: loc() };
    case 'hover':
      return { ...base, type: 'hover', locator: loc() };
    case 'click':
      return { ...base, type: 'click', locator: loc() };
    case 'type': {
      if (typeof value.text !== 'string') {
        throw new TourValidationError('expected string', `${path}.text`);
      }
      if (value.text.length > strMax) {
        throw new TourValidationError(`string exceeds limit of ${strMax} chars`, `${path}.text`);
      }
      const step: TypeStep = {
        ...base,
        type: 'type',
        locator: loc(),
        text: value.text,
      };
      if ('perCharMs' in value) step.perCharMs = optionalNumber(value.perCharMs, `${path}.perCharMs`, msMax);
      if ('clear' in value) step.clear = optionalBoolean(value.clear, `${path}.clear`);
      if ('textTpl' in value && value.textTpl !== undefined) {
        validateExpr(value.textTpl, `${path}.textTpl`, 0, limits);
        step.textTpl = value.textTpl as Expr;
      }
      return step;
    }
    case 'press': {
      const key = requireString(value.key, `${path}.key`, idMax);
      if (!PRESS_KEY_WHITELIST.has(key)) {
        throw new TourValidationError(
          `press key "${key}" is not in the allow-list`,
          `${path}.key`,
        );
      }
      const pressStep: PressStep = { ...base, type: 'press', key };
      if ('locator' in value && value.locator !== undefined) {
        pressStep.locator = loc();
      }
      return pressStep;
    }
    case 'select': {
      const step: SelectStep = { ...base, type: 'select', locator: loc() };
      if ('value' in value) step.value = optionalString(value.value, `${path}.value`, strMax);
      if ('selectOption' in value) {
        step.selectOption = optionalString(value.selectOption, `${path}.selectOption`, strMax);
      }
      if (step.value === undefined && step.selectOption === undefined) {
        throw new TourValidationError('select step requires either "value" or "selectOption"', path);
      }
      return step;
    }
    case 'waitFor':
      return { ...base, type: 'waitFor', locator: loc() };
    case 'wait': {
      const ms = optionalNumber(value.ms, `${path}.ms`, msMax);
      if (ms === undefined || ms < 0) {
        throw new TourValidationError('wait step requires non-negative "ms"', `${path}.ms`);
      }
      return { ...base, type: 'wait', ms };
    }
  }
}

/* ─────────────────────────── tour-level ───────────────────────────── */

function validateMatch(value: unknown, path: string, limits: EtlLimits): TourMatch | undefined {
  if (value === undefined) return undefined;
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected object', path);
  }
  const match: TourMatch = {};
  if ('route' in value) match.route = optionalString(value.route, `${path}.route`, limits.maxStringLength);
  if ('routePattern' in value) {
    match.routePattern = optionalString(value.routePattern, `${path}.routePattern`, limits.maxStringLength);
  }
  return match;
}

function validateDemoMode(value: unknown, path: string): DemoMode | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'string') {
    throw new TourValidationError('expected string', path);
  }
  if (value !== 'dry-run' && value !== 'sandbox') {
    throw new TourValidationError(`demoMode must be "dry-run" or "sandbox" (got "${value}")`, path);
  }
  return value;
}

function validatePace(value: unknown, path: string, limits: EtlLimits): TourPace | undefined {
  if (value === undefined) return undefined;
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected object', path);
  }
  const pace: TourPace = {};
  const max = limits.maxWaitMs;
  if ('cursorMs' in value) pace.cursorMs = optionalNumber(value.cursorMs, `${path}.cursorMs`, max);
  if ('perCharMs' in value) pace.perCharMs = optionalNumber(value.perCharMs, `${path}.perCharMs`, max);
  if ('settleMs' in value) pace.settleMs = optionalNumber(value.settleMs, `${path}.settleMs`, max);
  if ('narrateMs' in value) pace.narrateMs = optionalNumber(value.narrateMs, `${path}.narrateMs`, max);
  return pace;
}

function validateLocales(
  value: unknown,
  path: string,
  stepCount: number,
  limits: EtlLimits,
): Record<string, TourLocaleOverrides> | undefined {
  if (value === undefined) return undefined;
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected object', path);
  }
  const keys = Object.keys(value);
  if (keys.length > limits.maxLocales) {
    throw new TourValidationError(`too many locales (>${limits.maxLocales})`, path);
  }
  const out: Record<string, TourLocaleOverrides> = {};
  for (const key of keys) {
    if (!/^[a-zA-Z]{2,3}(?:-[a-zA-Z]{2,4})?$/.test(key)) {
      throw new TourValidationError(`invalid locale key "${key}" (BCP-47 expected)`, `${path}.${key}`);
    }
    const v = (value as Record<string, unknown>)[key];
    out[key] = validateLocaleOverrides(v, `${path}.${key}`, stepCount, limits);
  }
  return out;
}

function validateLocaleOverrides(
  value: unknown,
  path: string,
  stepCount: number,
  limits: EtlLimits,
): TourLocaleOverrides {
  if (!isPlainObject(value)) {
    throw new TourValidationError('expected object', path);
  }
  const out: TourLocaleOverrides = {};
  if ('title' in value) out.title = optionalString(value.title, `${path}.title`, limits.maxStringLength);
  if ('summary' in value) out.summary = optionalString(value.summary, `${path}.summary`, limits.maxStringLength);
  if ('steps' in value) {
    if (!Array.isArray(value.steps)) {
      throw new TourValidationError('expected array', `${path}.steps`);
    }
    if (value.steps.length > stepCount) {
      throw new TourValidationError(
        `locale.steps length (${value.steps.length}) exceeds tour step count (${stepCount})`,
        `${path}.steps`,
      );
    }
    out.steps = value.steps.map((s, i) => {
      if (s === undefined || s === null) return undefined;
      if (!isPlainObject(s)) {
        throw new TourValidationError('expected object', `${path}.steps[${i}]`);
      }
      const stepLoc: TourStepLocale = {};
      if ('narrate' in s) {
        stepLoc.narrate = optionalString(s.narrate, `${path}.steps[${i}].narrate`, limits.maxStringLength);
      }
      return stepLoc;
    });
  }
  return out;
}

/* ─────────────────────── deep-freeze (immutability) ─────────────────── */

function deepFreeze<T>(o: T): T {
  if (o === null || (typeof o !== 'object' && typeof o !== 'function')) return o;
  if (Object.isFrozen(o)) return o;
  Object.freeze(o);
  for (const k of Object.keys(o as object)) {
    deepFreeze((o as Record<string, unknown>)[k]);
  }
  return o;
}

/* ───────────────────────── public entry point ───────────────────────── */

export interface ValidateTourOptions {
  /** Override default {@link EtlLimits}. Falls back to {@link DEFAULT_LIMITS}. */
  readonly limits?: EtlLimits;
  /**
   * If false, the returned object is *not* deep-frozen. Default: true.
   * Frozen output is the safe default; opt out only for tooling that
   * needs to mutate (e.g. attaching a locale overlay) and re-freezes.
   */
  readonly freeze?: boolean;
}

export function validateTour(input: unknown, options: ValidateTourOptions = {}): Tour {
  const limits = options.limits ?? DEFAULT_LIMITS;

  if (!isPlainObject(input)) {
    throw new TourValidationError('tour must be an object', '');
  }
  const isLibrary = input.isLibrary === true;
  if (!Array.isArray(input.steps) || (input.steps.length === 0 && !isLibrary)) {
    throw new TourValidationError('tour must have a non-empty "steps" array', 'steps');
  }
  if (input.steps.length > limits.maxSteps) {
    throw new TourValidationError(
      `tour has ${input.steps.length} steps; limit is ${limits.maxSteps}`,
      'steps',
    );
  }

  const idMax = limits.maxIdentifierLength;
  const strMax = limits.maxStringLength;

  const tour: Tour = {
    id: requireString(input.id, 'id', idMax),
    pluginId: requireString(input.pluginId, 'pluginId', idMax),
    title: requireString(input.title, 'title', strMax),
    summary: optionalString(input.summary, 'summary', strMax),
    guidePath: optionalString(input.guidePath, 'guidePath', strMax),
    match: validateMatch(input.match, 'match', limits),
    demoMode: validateDemoMode(input.demoMode, 'demoMode'),
    pace: validatePace(input.pace, 'pace', limits),
    steps: [],
  };

  if ('etlVersion' in input && input.etlVersion !== undefined) {
    const v = input.etlVersion;
    if (v !== 1 && v !== 3) {
      throw new TourValidationError(
        `etlVersion must be 1 or 3 (got ${JSON.stringify(v)})`,
        'etlVersion',
      );
    }
    tour.etlVersion = v;
  }

  if ('onError' in input && input.onError !== undefined) {
    if (input.onError !== 'skip' && input.onError !== 'abort') {
      throw new TourValidationError(
        `onError must be "skip" or "abort" (got ${JSON.stringify(input.onError)})`,
        'onError',
      );
    }
    tour.onError = input.onError;
  }

  if (isLibrary) tour.isLibrary = true;

  if ('params' in input && input.params !== undefined) {
    tour.params = validateParamDecls(input.params, 'params', limits);
  }
  if ('requires' in input && input.requires !== undefined) {
    tour.requires = validateRequirements(input.requires, 'requires', limits);
  }
  if ('onlyFor' in input && input.onlyFor !== undefined) {
    tour.onlyFor = validateScopeFilter(input.onlyFor, 'onlyFor', limits);
  }
  if ('notFor' in input && input.notFor !== undefined) {
    tour.notFor = validateScopeFilter(input.notFor, 'notFor', limits);
  }
  if ('prerequisites' in input && input.prerequisites !== undefined) {
    if (!Array.isArray(input.prerequisites)) {
      throw new TourValidationError('expected array', 'prerequisites');
    }
    tour.prerequisites = input.prerequisites.map((id, i) =>
      requireString(id, `prerequisites[${i}]`, idMax),
    );
  }

  if ('macros' in input && input.macros !== undefined) {
    tour.macros = validateMacroDefs(input.macros, 'macros', limits);
  }
  if ('includes' in input && input.includes !== undefined) {
    if (!Array.isArray(input.includes)) {
      throw new TourValidationError('expected array', 'includes');
    }
    tour.includes = input.includes.map((p, i) =>
      requireString(p, `includes[${i}]`, limits.maxStringLength),
    );
  }

  tour.steps = input.steps.map((step, i) => {
    const validated = validateStep(step, `steps[${i}]`, limits);
    // Backfill an auto stableId for any step that arrived without one.
    // Both `.etl` (parser-built) and `.tour.json` (validator-only) tours
    // pass through here so the v2 locale-overlay scheme — which keys on
    // stableId — sees a uniform shape.
    if (validated.stableId === undefined) {
      validated.stableId = validated.id ?? `auto:${i.toString(36)}`;
    }
    return validated;
  });
  const locales = validateLocales(input.locales, 'locales', tour.steps.length, limits);
  if (locales) tour.locales = locales;

  return options.freeze === false ? tour : deepFreeze(tour);
}
